import { useState, useCallback, useEffect } from 'react';

/**
 * usePortfolioModals
 * 
 * Custom hook for managing portfolio modal state and navigation.
 * Handles the complex modal flow for account and transaction management.
 * 
 * Persists modal state to sessionStorage to survive tab switches and component remounts.
 */

export type PortfolioModalView =
  | 'list'
  | 'addAccountType'
  | 'addManualAccount'
  | 'linkBrokerage'
  | 'addTransactionOptions'
  | 'addManualTransaction'
  | 'editAccountTransactions';

// SessionStorage keys for modal state persistence
const STORAGE_KEY_IS_OPEN = 'vester:portfolio:modal:isOpen';
const STORAGE_KEY_VIEW = 'vester:portfolio:modal:view';
const STORAGE_KEY_EDITING_ACCOUNT_ID = 'vester:portfolio:modal:editingAccountId';
const STORAGE_KEY_EDITING_TRANSACTION_ID = 'vester:portfolio:modal:editingTransactionId';

export interface UsePortfolioModalsReturn {
  // Modal state
  isPortfolioModalOpen: boolean;
  portfolioModalView: PortfolioModalView;
  editingAccountId: string | null;
  editingTransactionId: string | null;
  showTopLevelCsvModal: boolean;

  // Modal navigation
  openPortfolioModal: () => void;
  closePortfolioModal: () => void;
  setPortfolioModalView: (view: PortfolioModalView) => void;
  setEditingAccountId: (id: string | null) => void;
  setEditingTransactionId: (id: string | null) => void;

  // Specific modal actions
  openAddAccountType: () => void;
  openAddManualAccount: () => void;
  openLinkBrokerage: () => void;
  openAddTransactionOptions: (accountId: string) => void;
  openAddManualTransaction: (accountId: string) => void;
  openEditAccountTransactions: (accountId: string) => void;
  backToAccountList: () => void;

  // CSV modal
  openTopLevelCsvModal: () => void;
  closeTopLevelCsvModal: () => void;
}

export const usePortfolioModals = (): UsePortfolioModalsReturn => {
  // Initialize state from sessionStorage if available
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = sessionStorage.getItem(STORAGE_KEY_IS_OPEN);
    return saved === 'true';
  });

  const [portfolioModalView, setPortfolioModalView] = useState<PortfolioModalView>(() => {
    if (typeof window === 'undefined') return 'list';
    const saved = sessionStorage.getItem(STORAGE_KEY_VIEW);
    if (saved && ['list', 'addAccountType', 'addManualAccount', 'linkBrokerage', 'addTransactionOptions', 'addManualTransaction', 'editAccountTransactions'].includes(saved)) {
      return saved as PortfolioModalView;
    }
    return 'list';
  });

  const [editingAccountId, setEditingAccountId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const saved = sessionStorage.getItem(STORAGE_KEY_EDITING_ACCOUNT_ID);
    return saved || null;
  });

  const [editingTransactionId, setEditingTransactionId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const saved = sessionStorage.getItem(STORAGE_KEY_EDITING_TRANSACTION_ID);
    return saved || null;
  });

  const [showTopLevelCsvModal, setShowTopLevelCsvModal] = useState(false);

  // Persist isPortfolioModalOpen to sessionStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isPortfolioModalOpen) {
      sessionStorage.setItem(STORAGE_KEY_IS_OPEN, 'true');
    } else {
      sessionStorage.removeItem(STORAGE_KEY_IS_OPEN);
    }
  }, [isPortfolioModalOpen]);

  // Persist portfolioModalView to sessionStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(STORAGE_KEY_VIEW, portfolioModalView);
  }, [portfolioModalView]);

  // Persist editingAccountId to sessionStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (editingAccountId) {
      sessionStorage.setItem(STORAGE_KEY_EDITING_ACCOUNT_ID, editingAccountId);
    } else {
      sessionStorage.removeItem(STORAGE_KEY_EDITING_ACCOUNT_ID);
    }
  }, [editingAccountId]);

  // Persist editingTransactionId to sessionStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (editingTransactionId) {
      sessionStorage.setItem(STORAGE_KEY_EDITING_TRANSACTION_ID, editingTransactionId);
    } else {
      sessionStorage.removeItem(STORAGE_KEY_EDITING_TRANSACTION_ID);
    }
  }, [editingTransactionId]);

  // Open/close main portfolio modal
  const openPortfolioModal = useCallback(() => {
    setIsPortfolioModalOpen(true);
    setPortfolioModalView('list');
  }, []);

  const closePortfolioModal = useCallback(() => {
    setIsPortfolioModalOpen(false);
    setPortfolioModalView('list');
    setEditingAccountId(null);
    setEditingTransactionId(null);
    // Clear all persisted modal state on explicit close
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY_IS_OPEN);
      sessionStorage.removeItem(STORAGE_KEY_VIEW);
      sessionStorage.removeItem(STORAGE_KEY_EDITING_ACCOUNT_ID);
      sessionStorage.removeItem(STORAGE_KEY_EDITING_TRANSACTION_ID);
    }
  }, []);

  // Specific modal navigation actions
  const openAddAccountType = useCallback(() => {
    setPortfolioModalView('addAccountType');
  }, []);

  const openAddManualAccount = useCallback(() => {
    setPortfolioModalView('addManualAccount');
  }, []);

  const openLinkBrokerage = useCallback(() => {
    setPortfolioModalView('linkBrokerage');
  }, []);

  const openAddTransactionOptions = useCallback((accountId: string) => {
    setEditingAccountId(accountId);
    setEditingTransactionId(null);
    setPortfolioModalView('addTransactionOptions');
  }, []);

  const openAddManualTransaction = useCallback((accountId: string) => {
    setEditingAccountId(accountId);
    setEditingTransactionId(null);
    setPortfolioModalView('addManualTransaction');
  }, []);

  const openEditAccountTransactions = useCallback((accountId: string) => {
    setEditingAccountId(accountId);
    setEditingTransactionId(null);
    setPortfolioModalView('editAccountTransactions');
  }, []);

  const backToAccountList = useCallback(() => {
    setPortfolioModalView('list');
    setEditingAccountId(null);
    setEditingTransactionId(null);
  }, []);

  // CSV modal
  const openTopLevelCsvModal = useCallback(() => {
    setShowTopLevelCsvModal(true);
  }, []);

  const closeTopLevelCsvModal = useCallback(() => {
    setShowTopLevelCsvModal(false);
  }, []);

  return {
    // State
    isPortfolioModalOpen,
    portfolioModalView,
    editingAccountId,
    editingTransactionId,
    showTopLevelCsvModal,

    // Actions
    openPortfolioModal,
    closePortfolioModal,
    setPortfolioModalView,
    setEditingAccountId,
    setEditingTransactionId,

    // Specific actions
    openAddAccountType,
    openAddManualAccount,
    openLinkBrokerage,
    openAddTransactionOptions,
    openAddManualTransaction,
    openEditAccountTransactions,
    backToAccountList,

    // CSV modal
    openTopLevelCsvModal,
    closeTopLevelCsvModal,
  };
};
